import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { SlideOver, SlideOverFooter } from '@/components/ui/SlideOver';
import { Textarea } from '@/components/ui/Textarea';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import {
  useWithdrawConsent,
  type ConsentGivenByKind,
  type ConsentIdentityVerification,
  type PatientConsentCurrentRow,
} from '@/hooks/useConsentRecords';
import { STAFF_RECORD_METHODS, consentTypeIsRefusal, type ConsentRecordMethod } from '@/lib/consent/consentTypes';

const IDENTITY_METHODS: ConsentIdentityVerification[] = ['known_to_staff', 'id_document', 'mitid', 'other'];
const GIVEN_BY_KINDS: ConsentGivenByKind[] = ['patient', 'custody_holder', 'guardian', 'future_attorney'];

export function WithdrawConsentSlideOver({
  current,
  onClose,
}: {
  current: PatientConsentCurrentRow | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const withdraw = useWithdrawConsent();
  const [method, setMethod] = useState<ConsentRecordMethod>('oral');
  const [identity, setIdentity] = useState<ConsentIdentityVerification>('known_to_staff');
  const [givenByKind, setGivenByKind] = useState<ConsentGivenByKind>('patient');
  const [givenByName, setGivenByName] = useState('');
  const [note, setNote] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!current) return;
    setMethod('oral');
    setIdentity('known_to_staff');
    setGivenByKind('patient');
    setGivenByName('');
    setNote('');
    setSubmitted(false);
  }, [current]);

  const nameError =
    givenByKind !== 'patient' && !givenByName.trim() ? t('consent.register.errors.given_by_name_required') : undefined;
  const isRefusal = current?.consent_type ? consentTypeIsRefusal(current.consent_type) : false;

  const handleSave = async () => {
    setSubmitted(true);
    if (!current?.record_id || nameError) return;
    try {
      await withdraw.mutateAsync({
        p_record_id: current.record_id,
        p_method: method,
        p_identity_verification_method: identity,
        p_given_by_kind: givenByKind,
        ...(givenByKind !== 'patient' ? { p_given_by_name: givenByName.trim() } : {}),
        ...(note.trim() ? { p_note: note.trim() } : {}),
      });
      toast.success(t('consent.withdraw.saved'));
      onClose();
    } catch (err) {
      console.error('withdraw_consent failed', err);
      toast.error(t('consent.withdraw.save_error'));
    }
  };

  return (
    <SlideOver
      open={current !== null}
      onClose={onClose}
      title={isRefusal ? t('consent.withdraw.title_refusal') : t('consent.withdraw.title')}
      description={t('consent.withdraw.description')}
      dirty={note.length > 0 || givenByName.length > 0}
      footer={
        <SlideOverFooter
          onCancel={onClose}
          primary={
            <Button variant="danger" onClick={() => void handleSave()} loading={withdraw.isPending}>
              {isRefusal ? t('consent.withdraw.confirm_refusal') : t('consent.withdraw.confirm')}
            </Button>
          }
        />
      }
    >
      {current?.consent_type ? (
        <div className="space-y-s4">
          <p className={cn(TYPE.body, 'text-ds-text')}>
            {t(`consent.types.${current.consent_type}`)}
            {current.scope_recipient_name ? ` · ${current.scope_recipient_name}` : ''}
            {current.scope_purpose ? ` · ${current.scope_purpose}` : ''}
          </p>
          <div className="grid grid-cols-1 gap-s4 nav:grid-cols-2">
            <Select
              id="consent-withdraw-method"
              label={t('consent.fields.method')}
              value={method}
              onChange={(e) => setMethod(e.target.value as ConsentRecordMethod)}
            >
              {STAFF_RECORD_METHODS.map((m) => (
                <option key={m} value={m}>
                  {t(`consent.method.${m}`)}
                </option>
              ))}
            </Select>
            <Select
              id="consent-withdraw-identity"
              label={t('consent.fields.identity')}
              value={identity}
              onChange={(e) => setIdentity(e.target.value as ConsentIdentityVerification)}
            >
              {IDENTITY_METHODS.map((m) => (
                <option key={m} value={m}>
                  {t(`consent.identity.${m}`)}
                </option>
              ))}
            </Select>
          </div>
          <div className="grid grid-cols-1 gap-s4 nav:grid-cols-2">
            <Select
              id="consent-withdraw-given-by"
              label={t('consent.fields.given_by')}
              value={givenByKind}
              onChange={(e) => setGivenByKind(e.target.value as ConsentGivenByKind)}
            >
              {GIVEN_BY_KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`consent.given_by.${k}`)}
                </option>
              ))}
            </Select>
            {givenByKind !== 'patient' ? (
              <Input
                id="consent-withdraw-given-by-name"
                label={t('consent.fields.given_by_name')}
                value={givenByName}
                onChange={(e) => setGivenByName(e.target.value)}
                error={submitted ? nameError : undefined}
              />
            ) : null}
          </div>
          <Textarea
            id="consent-withdraw-note"
            label={`${t('consent.fields.note')} (${t('common.optional')})`}
            rows={3}
            maxLength={4000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      ) : null}
    </SlideOver>
  );
}
